// resources/js/product_customizer.jsx
import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    createContext,
    useContext,
} from 'react';
import {createRoot} from 'react-dom/client';
import {createPortal} from 'react-dom';

// ----------------------------------------------------
// Globals injected by storage/app/customizer.template.js
// ----------------------------------------------------
const getGlobals = () => {
    const data =
        typeof window !== 'undefined' ? window.customizerData || {} : {};
    const product =
        typeof window !== 'undefined' ? window.customizerProductData || {} : {};
    return {data, product};
};

// -----------------------------
// Utilities
// -----------------------------
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const ptToPx = (pt) => (pt || 0) * (96 / 72);
const currencyFormat = (amount, code) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code || 'USD',
        }).format(amount);
    } catch {
        return (amount || 0).toFixed(2);
    }
};

// slugify for stable font-family names
const slugify = (s) => (s || '')
    .toString()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

// Dynamic font loader with cache
const loadedFontFamilies = new Set();
const pendingFontLoads = new Map(); // family -> Promise
const ensureFontLoaded = async (name, fileUrl, family) => {
    if (!fileUrl || !family) return; // nothing to load
    if (loadedFontFamilies.has(family)) return;
    const existing = pendingFontLoads.get(family);
    if (existing) return existing;
    const promise = (async () => {
        try {
            if ('fonts' in document && typeof FontFace === 'function') {
                const ff = new FontFace(family, `url(${fileUrl})`);
                await ff.load();
                document.fonts.add(ff);
            } else {
                // Fallback: inject @font-face
                const style = document.createElement('style');
                style.type = 'text/css';
                style.appendChild(document.createTextNode(`@font-face{font-family:"${family}";src:url(${fileUrl}) format("truetype");font-display:swap;}`));
                document.head.appendChild(style);
            }
            loadedFontFamilies.add(family);
        } catch (e) {
            // swallow errors, fallback to system fonts
            console.warn('[PC] Font load failed', name, fileUrl, e);
        } finally {
            pendingFontLoads.delete(family);
        }
    })();
    pendingFontLoads.set(family, promise);
    return promise;
};

// Measure text width using a shared canvas (best-effort if font not loaded)
const makeTextMeasurer = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    return (text, sizePt, family, weight = 'normal') => {
        if (!ctx) return (text?.length || 0) * ptToPx(sizePt) * 0.6;
        const sizePx = ptToPx(sizePt);
        ctx.font = `${weight} ${sizePx}px ${family || 'Arial, sans-serif'}`;
        return ctx.measureText(text || '').width;
    };
};
const measureTextWidth = typeof window !== 'undefined' ? makeTextMeasurer() : () => 0;

// -----------------------------
// Context to share state between buttons and the popup
// -----------------------------
const PCtx = createContext(null);
const usePC = () => useContext(PCtx);

function ProductCustomizerProvider({children}) {
    const {data, product} = getGlobals();
    const settings = data?.settings || {};
    const customizer = product?.customizer || null;

    const [open, setOpen] = useState(false);
    const [activeSideId, setActiveSideId] = useState(
        () => customizer?.sides?.[0]?.id || null
    );
    // selections: { [sideId]: { type: 'text'|'image', text: string, image: {file, url} } }
    const [selections, setSelections] = useState({});
    const [qty, setQty] = useState(1);

    const sides = Array.isArray(customizer?.sides) ? customizer.sides : [];
    const activeSide = sides.find((s) => s.id === activeSideId) || sides[0] || null;

    const extraPrice = useMemo(() => {
        if (!sides.length) return 0;
        let sum = 0;
        for (const s of sides) {
            const sel = selections[s.id];
            const hasText = sel?.type === 'text' && Array.isArray(sel.lines) && sel.lines.some(l => (l.text || '').trim().length > 0);
            const hasImage = sel?.type === 'image' && !!sel.image?.url;
            if (hasText || hasImage) {
                const p = Number(s.price || 0);
                if (!isNaN(p)) sum += p;
            }
        }
        return sum;
    }, [selections, sides]);

    const value = {
        settings,
        customizer,
        open,
        setOpen,
        sides,
        activeSideId,
        setActiveSideId,
        activeSide,
        selections,
        setSelections,
        qty,
        setQty,
        extraPrice,
        currency: data?.currency?.code || 'USD',
    };

    return <PCtx.Provider value={value}>{children}</PCtx.Provider>;
}

// -----------------------------
// Button that mounts into each placeholder
// -----------------------------
function PersonalizeButton() {
    const { settings, customizer, setOpen } = usePC();
    if (!customizer) return null;
    const label = settings?.button_label || 'Personalize';
    const textColor = settings?.button_text_color || '#ffffff';
    const bgColor = settings?.button_bg_color || '#0d6efd';
    const btnStyle = useMemo(() => ({
        color: textColor,
        backgroundColor: bgColor,
        border: `1px solid ${bgColor}`,
    }), [textColor, bgColor]);

    const handleClick = React.useCallback(() => {
        const form = document.querySelector('form[data-cart-item-add]');
        if (!form) {
            setOpen(true);
            return;
        }

        form.querySelectorAll('input, select, textarea').forEach(el => {
            if (el.setCustomValidity) el.setCustomValidity('');
        });

        if (form.reportValidity ? form.reportValidity() : form.checkValidity()) {
            setOpen(true);
        } else {
            const firstInvalid = form.querySelector(':invalid');
            if (firstInvalid) {
                try { firstInvalid.focus({ preventScroll: true }); } catch {}
                firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        }
    }, [setOpen]);

    return (
        <button type="button" className="pc-btn button" onClick={handleClick} style={btnStyle}>
            {label}
        </button>
    );
}


// -----------------------------
// Modal (singleton-per-provider) via DOM Portal
// -----------------------------
function OverlayModal() {
    const {open, setOpen, settings, extraPrice, currency} = usePC();

    // Create a dedicated container for this portal instance
    const portalEl = useMemo(() => {
        const el = document.createElement('div');
        // no id on purpose — to avoid collisions if multiple providers exist
        document.body.appendChild(el);
        return el;
    }, []);

    // Cleanup portal node on unmount
    useEffect(() => {
        return () => {
            if (portalEl?.parentNode) {
                portalEl.parentNode.removeChild(portalEl);
            }
        };
    }, [portalEl]);

    // ESC and body scroll lock only while open
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [open, setOpen]);

    if (!open) return null;

    const isSmallScreen = window.innerWidth < 1000;

    const width = isSmallScreen
        ? '100%'
        : (settings?.popup_width || '50vw');

    const height = isSmallScreen
        ? '80%'
        : (settings?.popup_height || '50vh');

    return createPortal(
        <div
            className="pc-overlay"
            onMouseDown={(e) => {
                if (e.target.classList.contains('pc-overlay')) setOpen(false);
            }}
        >
            <div className="pc-modal" style={{width, height}}>
                <div className="pc-modal-header">
                    <div className="pc-title">Product Customizer</div>
                    <button
                        className="pc-close"
                        onClick={() => setOpen(false)}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className="pc-modal-body">
                    <CustomizerPopupBody/>
                </div>
                <div className="pc-modal-footer">
                    <FooterActions extraPrice={extraPrice} currency={currency}/>
                </div>
            </div>
        </div>,
        portalEl
    );
}

// Global full-screen loading overlay with centered spinner
function GlobalLoadingOverlay() {
    const portalEl = useMemo(() => {
        const el = document.createElement('div');
        document.body.appendChild(el);
        return el;
    }, []);

    useEffect(() => () => { try { document.body.removeChild(portalEl); } catch {} }, [portalEl]);

    return createPortal(
        <div className="pc-global-overlay" aria-live="polite" aria-busy="true">
            <div className="pc-spinner"/>
        </div>,
        portalEl
    );
}

// Notice modal for success/error messages after addToCart
function NoticeModal({ open, type = 'success', message, onClose }) {
    const portalEl = useMemo(() => {
        const el = document.createElement('div');
        document.body.appendChild(el);
        return el;
    }, []);

    useEffect(() => () => { try { document.body.removeChild(portalEl); } catch {} }, [portalEl]);

    if (!open) return null;
    const toneClass = type === 'error' ? 'error' : 'success';
    return createPortal(
        <div className="pc-notice-overlay" onMouseDown={(e)=>{ if (e.target.classList.contains('pc-notice-overlay')) onClose?.(); }}>
            <div className="pc-notice-modal" role="dialog" aria-modal="true" aria-live="polite">
                <div className={`pc-notice-header ${toneClass}`}>
                    <div className="pc-notice-title">{type === 'error' ? 'Error' : 'Success'}</div>
                    <button className="pc-close" aria-label="Close" onClick={onClose}>×</button>
                </div>
                <div className="pc-notice-body">
                    {message || (type === 'error' ? 'Operation failed' : 'Operation completed')}
                </div>
                <div className="pc-notice-footer">
                    {type === 'success' && (
                        <a href="/cart" className="pc-btn pc-btn-primary" style={{marginRight: '8px', textDecoration: 'none'}}>Cart</a>
                    )}
                    <button className="pc-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>,
        portalEl
    );
}

function FooterActions({extraPrice, currency}) {
    const {qty, setQty, selections, sides, setOpen, setSelections} = usePC();
    const disabled = false; // allow always; backend computes eligibility

    // Local UI state for loader and notice popup
    const [isAdding, setIsAdding] = useState(false);
    const [notice, setNotice] = useState({ open: false, type: 'success', message: '' });

    const collectProductOptions = () => {
        // Try to gather BC option selections from the product form
        const form = document.querySelector('.productView-options, form[action*="cart.php"]') || document.querySelector('form[data-cart-item-add]');
        const opts = [];
        if (!form) return opts;
        const optionInputs = form.querySelectorAll('[name^="attribute"]');
        optionInputs.forEach((el) => {
            const name = el.getAttribute('name');
            const m = name && name.match(/attribute\[(\d+)\]/);
            const option_id = m ? parseInt(m[1], 10) : null;
            if (!option_id) return;
            let val = null;
            if ((el.type === 'checkbox' || el.type === 'radio')) {
                if (!el.checked) return; // only chosen value
                val = el.value;
            } else {
                val = el.value;
            }
            if (val === null || val === undefined || val === '') return;
            opts.push({option_id, option_value: val});
        });
        return opts;
    };

    const getProductId = () => {
        const sources = [
            // 1) Customizer data
            () => window.customizerProductData?.customizer?.product_id,

            // 2) BigCommerce global
            () => window.__bc__?.productId,

            // 3) ProductView
            () => document.querySelector('.productView[data-entity-id]')?.dataset.entityId,

            // 4) YMM
            () => document.querySelector('.productView[data-ymm-product-id]')?.dataset.ymmProductId,

            // 5) Fallback input
            () => document.querySelector('.productView-options input[name="product_id"]')?.value,
        ];

        for (const getValue of sources) {
            const raw = getValue();
            if (raw == null || raw === '') continue;

            const id = Number(raw);
            if (!Number.isNaN(id)) {
                return id;
            }
        }

        return null;
    }

    const addToCart = async () => {
        try {
            setIsAdding(true);
            const customizerId = window.customizerProductData.customizer.id;
            const shopifyProductId = window.customizerProductData.customizer.shopify_product_id;

            if (!shopifyProductId) {
                setNotice({ open: true, type: 'error', message: 'Product ID not found' });
                return;
            }

            const urlBase = window.customizerData.url || '';
            const endpoint = urlBase + '/cart/add';

            const fd = new FormData();
            fd.append('customizer_id', String(customizerId));
            fd.append('shopify_product_id', String(shopifyProductId));
            fd.append('qty', String(qty));

            // Attach selections JSON and image files per side
            const selPayload = {};
            Object.entries(selections || {}).forEach(([sid, sel]) => {
                selPayload[sid] = {
                    type: sel.type,
                    lines: Array.isArray(sel.lines) ? sel.lines.map(l => ({
                        text: l.text || '',
                        size: l.size,
                        color: l.color,
                        fontFamily: l.fontFamily
                    })) : [],
                    image: sel.image ? {hasFile: !!sel.image.file, cropMeta: sel.image.cropMeta || null} : null,
                };
                if (sel.image && sel.image.file) {
                    fd.append(`files[${sid}]`, sel.image.file, sel.image.file.name || `side-${sid}.png`);
                }
            });
            fd.append('selections', JSON.stringify(selPayload));

            // 1. Get customizer hash and fee info from our backend
            const res = await fetch(endpoint, {method: 'POST', body: fd, credentials: 'same-origin'});
            const json = await res.json();
            if (!res.ok || !json || json.ok !== true) {
                const msg = (json && (json.error || json.message)) || `Request failed (${res.status})`;
                setNotice({ open: true, type: 'error', message: 'Add to cart failed: ' + msg });
                return;
            }

            const { hash, fee_variant_id } = json;

            // 2. Add products to Shopify cart via AJAX API
            const itemsToAdd = [];

            // Main product with customizer hash as property
            // We need variant ID and other form data from the page
            const productForm = document.querySelector('product-form form');
            const formData = productForm ? new FormData(productForm) : null;

            const rawVariantId = formData ? formData.get('id') : document.querySelector('[name="id"]')?.value;
            if (!rawVariantId) {
                setNotice({ open: true, type: 'error', message: 'Variant ID not found on page' });
                return;
            }
            // Ensure numeric ID (handle potential GID format)
            const variantId = String(rawVariantId).includes('/') ? String(rawVariantId).split('/').pop() : rawVariantId;

            // Collect other properties from the form
            const formProperties = {};
            if (formData) {
                for (let [key, value] of formData.entries()) {
                    // Skip standard Shopify form fields
                    if (['id', 'quantity', 'form_type', 'utf8', 'product-id', 'section-id'].includes(key)) {
                        continue;
                    }
                    // Shopify properties usually start with "properties[" or are just additional fields
                    let propKey = key;
                    if (key.startsWith('properties[')) {
                        propKey = key.substring(11, key.length - 1);
                    }
                    formProperties[propKey] = value;
                }
            }

            itemsToAdd.push({
                id: variantId,
                quantity: qty,
                properties: {
                    ...formProperties,
                    '_customizer_hash': hash,
                    'Customized': 'Yes'
                }
            });

            // Fee product if applicable
            if (fee_variant_id) {
                // Ensure numeric ID (handle potential GID format)
                const numericFeeVariantId = fee_variant_id.includes('/') ? fee_variant_id.split('/').pop() : fee_variant_id;
                itemsToAdd.push({
                    id: numericFeeVariantId,
                    quantity: qty,
                    properties: {
                        '_customizer_parent_hash': hash
                    }
                });
            }

            const cartRes = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: itemsToAdd })
            });

            if (!cartRes.ok) {
                const cartJson = await cartRes.json();
                throw new Error(cartJson.description || 'Failed to add to Shopify cart');
            }

            // Reset state
            setSelections({});
            setQty(1);

            // Show success notice (popup)
            setNotice({ open: true, type: 'success', message: 'Product added to cart successfully' });
        } catch (e) {
            console.error('Add to cart error', e);
            setNotice({ open: true, type: 'error', message: 'Add to cart error: ' + (e.message || 'Unknown error') });
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className="pc-footer">
            <div className="pc-summary">
                <span className="pc-summary-label">Extra:</span>
                <strong>{currencyFormat(extraPrice, currency)}</strong>
            </div>
            <div className="pc-actions">
                <input
                    type="number"
                    min={1}
                    value={qty}
                    onChange={(e) =>
                        setQty(clamp(parseInt(e.target.value || '1', 10) || 1, 1, 999))
                    }
                    className="pc-qty form-input"
                />
                <button
                    className="pc-btn pc-btn-primary"
                    disabled={disabled || isAdding}
                    onClick={addToCart}
                >
                    {isAdding ? (
                        <span style={{display:'inline-flex',alignItems:'center',gap:8}}>
                            <span className="pc-spinner pc-spinner-sm" aria-hidden="true"/>
                            Adding...
                        </span>
                    ) : 'Add to cart'}
                </button>
            </div>
            {isAdding && <GlobalLoadingOverlay/>}
            <NoticeModal
                open={notice.open}
                type={notice.type}
                message={notice.message}
                onClose={() => {
                    // Close notice; if it was a success message — reload the page
                    setNotice(prev => ({...prev, open: false}));
                    if (notice.type === 'success') {
                        setOpen(false);
                        window.location.reload();
                    }
                }}
            />
        </div>
    );
}

function CustomizerPopupBody() {
    const {sides, activeSideId, setActiveSideId, activeSide} = usePC();
    if (!sides.length) return <div className="pc-empty">No sides configured.</div>;
    return (
        <div className="pc-body">
            <div className="pc-tabs">
                {sides.map((s) => (
                    <button
                        key={s.id}
                        className={`pc-tab ${activeSideId === s.id ? 'active' : ''}`}
                        onClick={() => setActiveSideId(s.id)}
                    >
                        <span>{s.name || 'Side'}</span>
                        {Number(s.price) > 0 && (
                            <span className="pc-badge">+{Number(s.price).toFixed(2)}</span>
                        )}
                    </button>
                ))}
            </div>
            <div className="pc-panel">
                {activeSide ? (
                    <SidePanel side={activeSide}/>
                ) : (
                    <div className="pc-empty">Select a side</div>
                )}
            </div>
        </div>
    );
}

function SidePanel({side}) {
    const {selections, setSelections} = usePC();
    const types = Array.isArray(side?.allowed?.types) ? side.allowed.types : [];
    const canText = types.includes('text');
    const canImage = types.includes('image');
    const textLines = clamp(parseInt(side?.allowed?.text?.lines ?? 1, 10) || 1, 1, 100);
    const maxLen = (() => {
        const v = parseInt(side?.allowed?.text?.max_length, 10);
        return !isNaN(v) && v > 0 ? v : undefined;
    })();

    // Available options from backend
    const textCfg = side?.allowed?.text || {};
    const palette = Array.isArray(textCfg.palettes) ? textCfg.palettes : [];
    const fonts = Array.isArray(textCfg.fonts) ? textCfg.fonts : [];

    const firstColor = palette[0]?.hex || '#000000';
    const firstFont = fonts[0]?.name ? ('pcf-' + slugify(fonts[0].name)) : 'Arial, sans-serif';

    const sel = selections[side.id] || {
        type: canText ? 'text' : (canImage ? 'image' : null),
        lines: Array.from({length: textLines}, () => ({text: '', size: 24, color: firstColor, fontFamily: firstFont})),
        activeLine: 0,
        image: undefined,
    };

    // Ensure lines length matches current side config
    useEffect(() => {
        setSelections((s) => {
            const existing = s[side.id] || sel;
            const next = {...existing};
            if (!Array.isArray(next.lines)) next.lines = [];
            if (next.lines.length !== textLines) {
                const arr = Array.from({length: textLines}, (_, i) => {
                    const prev = next.lines[i];
                    return prev ? {
                        ...prev,
                        color: prev.color || firstColor,
                        fontFamily: prev.fontFamily || firstFont,
                        size: clamp(prev.size || 24, 1, 999),
                    } : {text: '', size: 24, color: firstColor, fontFamily: firstFont};
                });
                next.lines = arr;
            } else {
                // normalize existing lines to ensure defaults present
                next.lines = next.lines.map((ln) => ({
                    text: ln?.text || '',
                    size: clamp(ln?.size || 24, 1, 999),
                    color: ln?.color || firstColor,
                    fontFamily: ln?.fontFamily || firstFont,
                }));
            }
            if (typeof next.activeLine !== 'number') next.activeLine = 0;
            return {...s, [side.id]: next};
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textLines, side.id]);

    const setType = (type) => setSelections((s) => ({...s, [side.id]: {...(s[side.id] || sel), type}}));
    const setActiveLine = (i) => setSelections((s) => ({
        ...s,
        [side.id]: {...(s[side.id] || sel), activeLine: i, type: 'text'}
    }));
    const updateLine = (i, patch) => setSelections((s) => {
        const cur = s[side.id] || sel;
        const lines = [...(cur.lines || [])];
        lines[i] = {...lines[i], ...patch};
        return {...s, [side.id]: {...cur, lines, type: 'text'}};
    });

    const [crop, setCrop] = useState({open: false, url: null, file: null});

    const onImageChange = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setSelections((s) => {
            const prevUrl = s[side.id]?.image?.url;
            if (prevUrl) {
                try {
                    URL.revokeObjectURL(prevUrl);
                } catch {
                }
            }
            const cur = s[side.id] || sel;
            return {...s, [side.id]: {...cur, type: 'image', image: {file, url}}};
        });
        // open crop modal right after upload
        setCrop({open: true, url, file});
    };

    // Revoke on unmount of this panel (safety)
    useEffect(() => {
        return () => {
            const url = (selections[side.id] || sel)?.image?.url;
            if (url) {
                try {
                    URL.revokeObjectURL(url);
                } catch {
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const area = side?.edit_area || null; // {x,y,width,height} decimals
    // Background image loading state for this side
    const [bgLoading, setBgLoading] = useState(!!side?.background_path);
    const [bgError, setBgError] = useState(null);

    useEffect(() => {
        setBgError(null);
        const hasSrc = !!side?.background_path;
        setBgLoading(hasSrc);
    }, [side?.background_path, side?.id]);

    useEffect(() => {
        // Collect font families that are actually in use right now
        const usedFamilies = new Set();

        // 1) default font for this side (first in the list)
        if (fonts?.[0]?.name) {
            usedFamilies.add('pcf-' + slugify(fonts[0].name));
        }

        // 2) fonts already assigned to current lines (restored from state)
        const cur = selections[side.id];
        (cur?.lines || []).forEach(l => {
            if (l?.fontFamily) usedFamilies.add(l.fontFamily);
        });

        // Build a lookup: family -> { name, file } for quick ensureFontLoaded calls
        const map = new Map(
            (fonts || []).map(f => ['pcf-' + slugify(f.name), { name: f.name, file: f.file }])
        );

        // Trigger loading only for the families we actually need
        usedFamilies.forEach(fam => {
            const info = map.get(fam);
            if (info?.file) {
                ensureFontLoaded(info.name, info.file, fam);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [side.id, fonts, selections[side.id]?.lines]);

    return (
        <div className="pc-side" aria-busy={bgLoading ? 'true' : 'false'}>
            {bgLoading && (
                <div className="pc-loading-overlay" role="status" aria-live="polite">
                    <div className="pc-spinner"/>
                </div>
            )}
            {side.background_path && (
                <ScaledBgStage src={side.background_path} error={bgError} loading={bgLoading}
                                onLoadDone={() => {
                                    setBgLoading(false);
                                }}
                                onError={() => {
                                    setBgError('failed');
                                    setBgLoading(false);
                                }}>
                    {area && area.width > 0 && area.height > 0 && (
                        <div
                            className="pc-area"
                            style={{
                                left: area.x * 100 + '%',
                                top: area.y * 100 + '%',
                                width: area.width * 100 + '%',
                                height: area.height * 100 + '%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {sel.type === 'text' && (
                                <AreaPreview
                                    area={area}
                                    lines={sel.lines || []}
                                    onFitted={(sizes) => {
                                        if (!Array.isArray(sizes)) return;
                                        setSelections((s) => {
                                            const cur = s[side.id] || sel;
                                            const prevLines = cur.lines || [];
                                            const nextLines = prevLines.map((ln, idx) => {
                                                const fs = clamp(Math.round(sizes[idx] ?? (ln.size ?? 24)), 1, 999);
                                                return (typeof fs === 'number' && fs !== ln.size) ? {
                                                    ...ln,
                                                    size: fs
                                                } : ln;
                                            });
                                            // If nothing changed, avoid state churn
                                            let changed = false;
                                            for (let i = 0; i < prevLines.length; i++) {
                                                if (prevLines[i] !== nextLines[i]) {
                                                    changed = true;
                                                    break;
                                                }
                                            }
                                            if (!changed) return s;
                                            return {...s, [side.id]: {...cur, lines: nextLines}};
                                        });
                                    }}
                                />
                            )}
                            {sel.type === 'image' && sel.image?.url && (
                                <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <img src={sel.image.croppedUrl || sel.image.url} alt="preview"
                                         style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}/>
                                </div>
                            )}
                        </div>
                    )}
                </ScaledBgStage>
            )}

            <div className="pc-opts">
                <div className="pc-seg">
                    {canImage && <button className={`pc-seg-btn ${sel.type === 'image' ? 'active' : ''}`}
                                         onClick={() => setType('image')}>Photo/Logo</button>}
                    {canText && <button className={`pc-seg-btn ${sel.type === 'text' ? 'active' : ''}`}
                                        onClick={() => setType('text')}>Text</button>}
                </div>

                {canText && sel.type === 'text' && (
                    <div className="pc-block form-group">
                        <div className="pc-label form-label">Enter Text</div>
                        {Array.from({length: textLines}).map((_, i) => {
                            const line = sel.lines?.[i] || {
                                text: '',
                                size: 24,
                                color: '#000000',
                                fontFamily: 'Arial, sans-serif'
                            };
                            return (
                                <div key={i} className="pc-line form-group">
                                    <label className="pc-line-label form-label">Text
                                        Line {i + 1}{maxLen ? ` (limit ${maxLen} symbols)` : ''}</label>
                                    <input
                                        className="pc-input form-input"
                                        value={line.text}
                                        maxLength={maxLen || undefined}
                                        placeholder="Enter text"
                                        onFocus={() => setActiveLine(i)}
                                        onChange={(e) => updateLine(i, {text: e.target.value})}
                                    />
                                    {sel.activeLine === i && (
                                        <div className="pc-line-opts">
                                            <div className="pc-line-opt form-group form-option1">
                                                <label className="form-label">Font Size</label>
                                                <input className="form-input" type="number" min={1} max={999}
                                                       value={line.size}
                                                       onChange={(e) => updateLine(i, {size: clamp(parseInt(e.target.value || '24', 10) || 24, 1, 999)})}/>
                                            </div>
                                            <div className="pc-line-opt form-group form-option1">
                                                <label className="form-label">Font Color</label>
                                                <div className="pc-color-select">
                                                    <select className="form-select" value={line.color}
                                                            onChange={(e) => updateLine(i, {color: e.target.value})}
                                                            // disabled={!palette.length}
                                                    >
                                                        {palette.length ? (
                                                            palette.map((c, idx) => (
                                                                <option key={idx}
                                                                        value={c.hex}>{(c.name || '').trim() || c.hex}</option>
                                                            ))
                                                        ) : (
                                                            <option
                                                                value={line.color || '#000000'}>{line.color || '#000000'}</option>
                                                        )}
                                                    </select>
                                                    <span className="pc-swatch"
                                                          style={{background: line.color || '#000000'}}/>
                                                </div>
                                            </div>
                                            <div className="pc-line-opt form-group form-option1">
                                                <label className="form-label">Font</label>
                                                <select className="form-select" value={line.fontFamily}
                                                        onChange={async (e) => {
                                                            const val = e.target.value;
                                                            const opts = Array.isArray(fonts) ? fonts.map(f => ({
                                                                name: f.name,
                                                                file: f.file,
                                                                family: 'pcf-' + slugify(f.name)
                                                            })) : [];
                                                            const found = opts.find(o => o.family === val);
                                                            if (found && found.file) {
                                                                await ensureFontLoaded(found.name, found.file, found.family);
                                                            }
                                                            updateLine(i, {fontFamily: val});
                                                        }}
                                                        // disabled={!fonts.length}
                                                >
                                                    {fonts.length ? (
                                                        fonts.map((f, idx) => {
                                                            const fam = 'pcf-' + slugify(f.name);
                                                            return <option key={idx} value={fam}>{f.name}</option>;
                                                        })
                                                    ) : (
                                                        <option
                                                            value={line.fontFamily || 'Arial, sans-serif'}>{line.fontFamily?.split(',')[0] || 'Arial'}</option>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {canImage && sel.type === 'image' && (
                    <div className="pc-block form-group">
                        <div className="pc-label form-label">Image</div>
                        <input className="form-input" type="file" accept="image/*"
                               onChange={(e) => onImageChange(e.target.files?.[0])}/>
                        {sel.image?.url && (
                            <div className="pc-image-preview">
                                <img src={sel.image.croppedUrl || sel.image.url} alt="preview"/>
                                <div style={{marginTop: 6, display: 'flex', gap: 8}}>
                                    <button className="pc-btn" onClick={() => setCrop({
                                        open: true,
                                        url: sel.image.url,
                                        file: sel.image.file
                                    })}>Re-crop
                                    </button>
                                    {sel.image.croppedUrl && (
                                        <button className="pc-btn" onClick={() => {
                                            // remove crop, keep original
                                            setSelections(s => {
                                                const cur = s[side.id] || sel;
                                                const prev = cur.image;
                                                if (prev?.croppedUrl) {
                                                    try {
                                                        URL.revokeObjectURL(prev.croppedUrl);
                                                    } catch {
                                                    }
                                                }
                                                return {
                                                    ...s,
                                                    [side.id]: {
                                                        ...cur,
                                                        image: {...prev, croppedUrl: undefined, cropMeta: undefined}
                                                    }
                                                };
                                            });
                                        }}>Reset crop</button>
                                    )}
                                </div>
                            </div>
                        )}
                        {crop.open && crop.url && (
                            <ImageCropModal
                                imageUrl={crop.url}
                                initialShape={(selections[side.id]?.image?.cropMeta?.shape) || 'square'}
                                initialCropMeta={selections[side.id]?.image?.cropMeta || null}
                                onCancel={() => setCrop({open: false, url: null, file: null})}
                                onKeepOriginal={() => {
                                    setCrop({open: false, url: null, file: null});
                                    // keep as-is already saved in selection on upload
                                }}
                                onFinish={({blob, blobUrl, meta}) => {
                                    setCrop({open: false, url: null, file: null});
                                    setSelections(s => {
                                        const cur = s[side.id] || sel;
                                        const prev = cur.image || {};
                                        if (prev.croppedUrl) {
                                            try {
                                                URL.revokeObjectURL(prev.croppedUrl);
                                            } catch {
                                            }
                                        }
                                        return {
                                            ...s,
                                            [side.id]: {
                                                ...cur,
                                                type: 'image',
                                                image: {...prev, croppedUrl: blobUrl, cropMeta: meta}
                                            }
                                        };
                                    });
                                }}
                            />
                        )}
                    </div>
                )}

                {!canText && !canImage && (
                    <div className="pc-muted">This side has no editable options.</div>
                )}
            </div>
        </div>
    );
}

function useElementSize() {
    const ref = useRef(null);
    const [size, setSize] = useState({width: 0, height: 0});
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const cr = entries[0]?.contentRect;
            if (cr) setSize({width: cr.width, height: cr.height});
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, size];
}

function AreaPreview({area, lines, onFitted}) {
    // Measure available px from parent via a wrapper ref
    const [wrapRef, size] = useElementSize();
    const [fontTick, setFontTick] = useState(0);
    const maxW = size.width || 0;
    const maxH = size.height || 0;

    // Recalculate when fonts finish loading
    useEffect(() => {
        if (document && document.fonts && document.fonts.addEventListener) {
            const onDone = () => setFontTick((t) => t + 1);
            document.fonts.addEventListener('loadingdone', onDone);
            return () => document.fonts.removeEventListener('loadingdone', onDone);
        }
    }, []);

    // Compute fitted sizes per line
    const fitted = useMemo(() => {
        if (!maxW || !maxH) return lines.map((l) => ({...l, fittedSize: l.size}));
        // First, fit each line to width by decreasing font size
        const widthFitted = lines.map((l) => {
            let s = clamp(l.size || 24, 1, 999);
            const family = l.fontFamily || 'Arial, sans-serif';
            const text = (l.text || '').toString();
            if (!text.length) return {...l, fittedSize: s, _empty: true};
            let w = measureTextWidth(text, s, family);
            while (w > maxW && s > 1) {
                s -= 1;
                w = measureTextWidth(text, s, family);
            }
            return {...l, fittedSize: s, _empty: false};
        });
        // Then, ensure total height fits; assume 1.2 line-height in px
        const totalH = widthFitted.reduce((sum, l) => sum + (!l._empty ? ptToPx(l.fittedSize) * 1.2 : 0), 0);
        if (totalH <= maxH) return widthFitted;
        const scale = maxH / totalH;
        return widthFitted.map((l) => l._empty ? l : ({
            ...l,
            fittedSize: Math.max(1, Math.floor(l.fittedSize * scale))
        }));
    }, [lines, maxW, maxH, fontTick]);

    // Notify parent about fitted sizes so UI can sync font size controls
    useEffect(() => {
        if (typeof onFitted === 'function') {
            const sizes = (fitted || []).map(l => l?.fittedSize ?? l?.size ?? 24);
            onFitted(sizes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitted]);

    // Render centered block
    return (
        <div ref={wrapRef} className="pc-preview-wrap">
            <div className="pc-preview-block" style={{width: '100%', height: '100%'}}>
                {fitted.map((l, idx) => (
                    <div key={idx} className="pc-preview-line" style={{
                        color: l.color || '#000',
                        fontFamily: l.fontFamily || 'Arial, sans-serif',
                        fontSize: (l.fittedSize || l.size || 24) + 'pt',
                        lineHeight: 1.2,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                    }}>
                        {l.text}
                    </div>
                ))}
            </div>
        </div>
    );
}

// Utility img wrapper that triggers onLoad immediately for cached images
function BgImage({src, onLoad, onError}) {
    const ref = useRef(null);
    useEffect(() => {
        const img = ref.current;
        if (!img) return;
        // If the image is cached, onLoad may have fired before handlers attach
        if (img.complete) {
            // Defer to next frame to avoid state updates during render
            requestAnimationFrame(() => {
                if (img.naturalWidth > 0) {
                    onLoad && onLoad();
                } else {
                    onError && onError();
                }
            });
        }
    }, [src]);
    return <img ref={ref} src={src} alt="bg" onLoad={onLoad} onError={onError}/>;
}

// Context to provide scale/base to overlays
const ScaleCtx = React.createContext({ scale: 1, baseW: 0, baseH: 0 });

// Stage that keeps a fixed base coordinate space (natural image size) and scales via CSS transform
function ScaledBgStage({ src, children, onLoadDone, onError, loading, error }) {
    const [natural, setNatural] = useState({ w: 0, h: 0 });
    // Measure the available box (both width and height) to fit the background into
    const [wrapRef, wrapSize] = useElementSize();

    useEffect(() => {
        if (!src) return;
        const im = new Image();
        im.onload = () => {
            setNatural({ w: im.naturalWidth, h: im.naturalHeight });
            onLoadDone && onLoadDone();
        };
        im.onerror = () => onError && onError();
        im.src = src;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    const baseW = natural.w || 1;
    const baseH = natural.h || 1;
    const containerW = Math.max(1, wrapSize.width || 1);
    const containerH = Math.max(1, wrapSize.height || 1);
    // Scale to CONTAIN within both width and height of the available area
    const scale = Math.min(containerW / baseW, containerH / baseH);

    return (
        <div ref={wrapRef} className="pc-bg">
            {/* Fill all available space and center the scaled stage inside */}
            <div className="pc-stage-wrap" style={{ position: 'relative', width: '100%', height: '100%' }}>
                <ScaleCtx.Provider value={{ scale, baseW, baseH }}>
                    <div className="pc-stage" style={{
                        position: 'absolute', left: '50%', top: '50%',
                        width: baseW, height: baseH,
                        transform: `translate(-50%, -50%) scale(${scale})`, transformOrigin: 'center center',
                        opacity: loading ? 0 : 1,
                        pointerEvents: loading ? 'none' : 'auto',
                        transition: 'opacity 0.15s ease-out'
                    }}>
                        {/* Background image at natural resolution */}
                        <img src={src} alt="bg" style={{ width: baseW, height: baseH, display: 'block', border: '1px solid #eee', borderRadius: 4 }}/>
                        {/* Overlay content (areas) in percentage coords will map to base */}
                        {children}
                    </div>
                </ScaleCtx.Provider>
            </div>
            {error && !loading && (
                <div className="pc-loading-error">Image failed to load</div>
            )}
        </div>
    );
}

// ---------------------------------
// ImageCropModal — simple crop tool
// ---------------------------------
function ImageCropModal({
                            imageUrl,
                            initialShape = 'square',
                            initialCropMeta = null,
                            onCancel,
                            onKeepOriginal,
                            onFinish
                        }) {
    const portalEl = useMemo(() => {
        const el = document.createElement('div');
        document.body.appendChild(el);
        return el;
    }, []);
    useEffect(() => () => {
        if (portalEl.parentNode) portalEl.parentNode.removeChild(portalEl);
    }, [portalEl]);

    const [img, setImg] = useState(null);
    const [shape, setShape] = useState(initialShape);
    const [wrapRef, wrapSize] = useElementSize();
    const [view, setView] = useState({x: 0, y: 0, w: 0, h: 0, scale: 1}); // image displayed box within work area
    const [rect, setRect] = useState({x: 0, y: 0, w: 0, h: 0}); // in view coords

    // load image
    useEffect(() => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => setImg({el: im, naturalW: im.naturalWidth, naturalH: im.naturalHeight});
        im.src = imageUrl;
    }, [imageUrl]);

    // compute view box to contain image
    useEffect(() => {
        if (!img || !wrapSize.width || !wrapSize.height) return;
        const iw = img.naturalW, ih = img.naturalH;
        const ar = iw / ih;
        let vw = wrapSize.width, vh = wrapSize.height;
        let w = vw, h = Math.round(vw / ar);
        if (h > vh) {
            h = vh;
            w = Math.round(vh * ar);
        }
        const x = Math.round((vw - w) / 2), y = Math.round((vh - h) / 2);
        const scale = w / iw; // displayed px per image px
        setView({x, y, w, h, scale});
        // default crop rect
        if (initialCropMeta) {
            const rx = Math.round(initialCropMeta.x * scale) + x;
            const ry = Math.round(initialCropMeta.y * scale) + y;
            const rw = Math.round(initialCropMeta.w * scale);
            const rh = Math.round(initialCropMeta.h * scale);
            setRect({x: rx, y: ry, w: rw, h: rh});
        } else {
            const side = Math.round(Math.min(w, h) * 0.7);
            const cx = Math.round(x + (w - side) / 2);
            const cy = Math.round(y + (h - side) / 2);
            setRect({x: cx, y: cy, w: side, h: side});
        }
    }, [img, wrapSize.width, wrapSize.height]);

    // interactions
    const actionRef = useRef(null); // { type:'move'|'n'|'ne'|..., start:{mx,my}, rect }

    const onPointerDown = (e, type = 'move') => {
        e.preventDefault();
        const mx = e.clientX;
        const my = e.clientY;
        actionRef.current = {type, start: {mx, my}, rect: {...rect}};
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp, {once: true});
    };
    const clampRect = (r) => {
        // keep inside view box
        const min = 20; // px
        const vx = view.x, vy = view.y, vw = view.w, vh = view.h;
        r.w = Math.max(min, Math.min(r.w, vw - (r.x - vx)));
        r.h = Math.max(min, Math.min(r.h, vh - (r.y - vy)));
        r.x = Math.max(vx, Math.min(r.x, vx + vw - r.w));
        r.y = Math.max(vy, Math.min(r.y, vy + vh - r.h));
        return r;
    };
    const onPointerMove = (e) => {
        const st = actionRef.current;
        if (!st) return;
        const dx = e.clientX - st.start.mx;
        const dy = e.clientY - st.start.my;
        let r = {...st.rect};
        switch (st.type) {
            case 'move':
                r.x += dx;
                r.y += dy;
                break;
            case 'n':
                r.y += dy;
                r.h -= dy;
                break;
            case 's':
                r.h += dy;
                break;
            case 'w':
                r.x += dx;
                r.w -= dx;
                break;
            case 'e':
                r.w += dx;
                break;
            case 'nw':
                r.x += dx;
                r.w -= dx;
                r.y += dy;
                r.h -= dy;
                break;
            case 'ne':
                r.y += dy;
                r.h -= dy;
                r.w += dx;
                break;
            case 'sw':
                r.x += dx;
                r.w -= dx;
                r.h += dy;
                break;
            case 'se':
                r.w += dx;
                r.h += dy;
                break;
        }
        setRect(clampRect(r));
    };
    const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        actionRef.current = null;
    };

    // export crop
    const finish = async () => {
        if (!img) return;
        // convert rect from view coords to image pixel coords
        const scale = 1 / (view.scale || 1);
        const x = Math.round((rect.x - view.x) * scale);
        const y = Math.round((rect.y - view.y) * scale);
        const w = Math.max(1, Math.round(rect.w * scale));
        const h = Math.max(1, Math.round(rect.h * scale));
        const meta = {x, y, w, h, shape, naturalW: img.naturalW, naturalH: img.naturalH};

        // prepare canvas
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const outW = Math.round(w * dpr);
        const outH = Math.round(h * dpr);
        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.save();
        if (shape === 'circle') {
            // Elliptical clip (allows oval)
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.beginPath();
            ctx.ellipse(canvas.width / 2, canvas.height / 2, canvas.width / 2, canvas.height / 2, 0, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
        }
        // Draw the selected source rect scaled to the canvas
        ctx.drawImage(img.el, x, y, w, h, 0, 0, outW, outH);
        ctx.restore();
        await new Promise(resolve => canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            onFinish && onFinish({blob, blobUrl: url, meta});
            resolve();
        }, 'image/png'));
    };

    const stop = (e) => e.stopPropagation();

    const content = (
        <div className="pc-crop-overlay" onMouseDown={onCancel}>
            <div className="pc-crop-modal" onMouseDown={stop}>
                <div className="pc-crop-header"><strong className="me-auto">Image Crop</strong>
                    <button className="pc-close" onClick={onCancel}>×</button>
                </div>
                <div className="pc-crop-cols">
                    <div className="pc-crop-left">
                        <div className="pc-label">Your image</div>
                        <div className="pc-crop-work" style={{minHeight: 200}}>
                            <img className="pc-crop-img" src={imageUrl} alt="original"/>
                        </div>
                        <button className="pc-btn" onClick={() => {
                            onKeepOriginal && onKeepOriginal();
                        }}>Correct (don’t edit)
                        </button>
                    </div>
                    <div className="pc-crop-right">
                        <div className="pc-label">Edit</div>
                        <div ref={wrapRef} className="pc-crop-work" onPointerDown={(e) => {
                            if (e.target.closest('.pc-crop-handle')) return;
                            if (!e.target.closest('.pc-crop-rect')) return;
                            onPointerDown(e, 'move');
                        }}>
                            {img && (
                                <>
                                    <img className="pc-crop-img" src={imageUrl} alt="to-crop" style={{
                                        position: 'absolute',
                                        left: view.x,
                                        top: view.y,
                                        width: view.w,
                                        height: view.h
                                    }}/>
                                    <div className={`pc-crop-rect ${shape === 'circle' ? 'circle' : ''}`}
                                         style={{left: rect.x, top: rect.y, width: rect.w, height: rect.h}}>
                                        {/* handles */}
                                        {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((p, idx) => {
                                            const pos = {
                                                nw: {left: -6, top: -6},
                                                n: {left: '50%', top: -6, transform: 'translateX(-50%)'},
                                                ne: {right: -6, top: -6},
                                                e: {right: -6, top: '50%', transform: 'translateY(-50%)'},
                                                se: {right: -6, bottom: -6},
                                                s: {left: '50%', bottom: -6, transform: 'translateX(-50%)'},
                                                sw: {left: -6, bottom: -6},
                                                w: {left: -6, top: '50%', transform: 'translateY(-50%)'}
                                            }[p];
                                            return <div key={idx} className="pc-crop-handle" style={pos}
                                                        onPointerDown={(e) => {
                                                            e.stopPropagation();
                                                            onPointerDown(e, p);
                                                        }}>&nbsp;</div>
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="pc-crop-controls">
                            <div className="pc-seg">
                                <button className={`pc-seg-btn ${shape === 'square' ? 'active' : ''}`}
                                        onClick={() => setShape('square')}>SQUARE
                                </button>
                                <button className={`pc-seg-btn ${shape === 'circle' ? 'active' : ''}`}
                                        onClick={() => setShape('circle')}>CIRCLE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pc-crop-footer">
                    <button className="pc-btn" onClick={onCancel}>Cancel</button>
                    <button className="pc-btn pc-btn-primary" onClick={finish}>Finished ✓</button>
                </div>
            </div>
        </div>
    );

    return createPortal(content, portalEl);
}

// -----------------------------
// Styles (scoped minimal CSS)
// -----------------------------
function injectStyles() {
    const id = 'pc-styles';
    if (document.getElementById(id)) return;

    const css = `
.pc-btn{display:inline-block;background:#0d6efd;color:#ffffff;border:1px solid #0d6efd;border-radius:4px;padding:8px 12px;font:inherit;cursor:pointer}
.pc-btn:hover{background:#0b5ed7;border-color:#0a58ca}
.pc-btn-primary{background:#198754;border-color:#198754}
.pc-btn-primary:hover{background:#157347;border-color:#146c43}

.pc-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:2147484000}
.pc-modal{background:#ffffff;border-radius:8px;box-shadow:0 10px 30px rgba(0,0,0,.2);display:flex;flex-direction:column;max-width:95vw;max-height:90vh;overflow:hidden}
.pc-modal-header{display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #eee}
.pc-title{font-weight:600}
.pc-close{margin-left:auto;background:transparent;border:0;font-size:20px;line-height:1;cursor:pointer}
.pc-modal-body{flex:1 1 auto;min-height:0;overflow:hidden}
.pc-modal-footer{border-top:1px solid #eee;padding:8px}

.pc-body{display:flex;flex-direction:column;height:100%;min-height:0}
.pc-tabs{display:flex;gap:6px;flex-wrap:wrap;padding:8px;border-bottom:1px solid #f2f2f2}
.pc-tab{border:1px solid #ddd;background:#fafafa;padding:6px 10px;border-radius:4px;cursor:pointer}
.pc-tab.active{background:#0d6efd;color:#ffffff;border-color:#0d6efd}
.pc-badge{margin-left:6px;background:rgba(255,255,255,.2);padding:2px 6px;border-radius:10px;font-size:12px}
.pc-panel{flex:1 1 auto;padding:12px;height:auto;min-height:0;box-sizing:border-box}
.pc-side{position:relative;display:grid;grid-template-columns:1.2fr .8fr;gap:16px;height:100%;min-height:0}

.pc-bg{position:relative;width:100%;height:100%;min-height:300px;overflow:hidden}
.pc-stage-wrap{position:relative;width:100%;height:100%;min-height:300px}
.pc-bg>img{width:100%;height:auto;display:block;border:1px solid #eee;border-radius:4px}
.pc-area{position:absolute;border:2px dashed #0d6efd;background:rgba(13,110,253,.08);display:block!important}

.pc-loading-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.9);z-index:2;pointer-events:auto}
.pc-loading-error{position:absolute;left:8px;bottom:8px;background:rgba(220,53,69,.9);color:#ffffff;padding:2px 6px;border-radius:4px;font-size:12px;z-index:3}
.pc-spinner{width:36px;height:36px;border:3px solid rgba(13,110,253,.25);border-top-color:#0d6efd;border-radius:50%;animation:pc-spin .9s linear infinite}
.pc-spinner-sm{width:18px;height:18px;border-width:2px}
@keyframes pc-spin{to{transform:rotate(360deg)}}

.pc-opts{display:flex;flex-direction:column;gap:12px;overflow:auto;min-height:0}
.pc-label{margin-bottom:4px}
.pc-empty,.pc-muted{color:#6c757d}
.pc-image-preview img{max-width:100%;height:auto;border:1px solid #eee;border-radius:4px;margin-top:8px}

.pc-input,
.pc-line-opt .form-input,
.pc-line-opt .form-select{width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:6px;background:#ffffff;font-size:14px;line-height:1.4;transition:border-color .2s,box-shadow .2s}

.pc-input:hover,
.pc-line-opt .form-input:hover,
.pc-line-opt .form-select:hover{border-color:#9ca3af}

.pc-input:focus,
.pc-line-opt .form-input:focus,
.pc-line-opt .form-select:focus{outline:none;border-color:#0d6efd;box-shadow:0 0 0 2px rgba(13,110,253,.2)}

.pc-input::placeholder{color:#9ca3af}

.pc-input:disabled,
.pc-line-opt .form-input:disabled,
.pc-line-opt .form-select:disabled{background:#f3f4f6;cursor:not-allowed;opacity:.7}

.pc-line-opt input[type="number"]{-moz-appearance:textfield}
.pc-line-opt input[type="number"]::-webkit-outer-spin-button,
.pc-line-opt input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}

.pc-line-opt .form-select{width:100%;padding:8px 30px 8px 10px;border:1px solid #d1d5db;border-radius:6px;background:#ffffff;font-size:14px;cursor:pointer;appearance:none;-webkit-appearance:none;-moz-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 8 10 12 14 8'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 8px center;background-size:16px}

.pc-footer{display:flex;align-items:center;gap:12px;justify-content:space-between}
.pc-summary{display:flex;align-items:center;gap:6px}
.pc-actions{display:flex;align-items:center;gap:8px}
.pc-qty{width:72px;padding:6px;border:1px solid #ccc;border-radius:4px}

.pc-seg{display:inline-flex;border:1px solid #ddd;border-radius:6px;align-self:flex-start}
.pc-seg-btn{background:#f7f7f7;border:0;padding:6px 10px;cursor:pointer;border-radius:4px}
.pc-seg-btn+.pc-seg-btn{border-left:1px solid #ddd}
.pc-seg-btn.active{background:#0d6efd;color:#ffffff}

.pc-line{margin-bottom:10px}
.pc-line-label{display:block;font-size:12px;color:#666;margin-bottom:4px}
.pc-line-opts{margin-top:10px;padding:12px;border:1px solid #e5e7eb;border-radius:8px;background:#fafafa;display:flex;flex-direction:column;gap:10px;align-items:stretch;overscroll-behavior:contain}
.pc-line-opts:after{content:'';position:sticky;bottom:-12px;display:block;height:18px;margin:0 -12px -12px;background:linear-gradient(to bottom,rgba(250,250,250,0),#fafafa);pointer-events:none}
.pc-line-opt{display:flex;align-items:center;justify-content:space-between;gap:12px;font-size:12px;color:#555}
.pc-line-opt .form-label{margin:0;min-width:80px;color:#374151}
.pc-line-opt .form-input,
.pc-line-opt .form-select,
.pc-line-opt .pc-color-select{flex:1 1 auto}
.pc-color-select{display:flex;align-items:center;gap:8px;width:100%}
.pc-color-select select{width:100%;min-width:60px}
.pc-swatch{display:inline-block;width:20px;height:20px;border:1px solid #d1d5db;border-radius:4px;box-shadow:inset 0 0 0 1px rgba(255,255,255,.4)}

.pc-preview-wrap{position:relative;display:flex;align-items:center;justify-content:center;width:100%;height:100%}
.pc-preview-block{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;width:100%;height:100%}
.pc-preview-line{display:block}

.pc-global-overlay{position:fixed;inset:0;background:rgba(255,255,255,.4);backdrop-filter:saturate(180%) blur(2px);display:flex;align-items:center;justify-content:center;z-index:2147484300}

.pc-notice-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:2147484400}
.pc-notice-modal{background:#ffffff;border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.25);width:min(90vw,460px);overflow:hidden}
.pc-notice-header{display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #eee}
.pc-notice-header.success{background:#d1e7dd;color:#0f5132;border-bottom-color:#badbcc}
.pc-notice-header.error{background:#f8d7da;color:#842029;border-bottom-color:#f5c2c7}
.pc-notice-title{font-weight:600}
.pc-notice-body{padding:16px;font-size:14px;color:#333}
.pc-notice-footer{display:flex;justify-content:flex-end;gap:8px;padding:10px 12px;border-top:1px solid #eee}

.pc-crop-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;z-index:2147484200}
.pc-crop-modal{background:#ffffff;border-radius:8px;box-shadow:0 10px 30px rgba(0,0,0,.35);display:flex;flex-direction:column;width:min(90vw,1000px);height:min(85vh,720px);overflow:hidden}
.pc-crop-header{display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #eee}
.pc-crop-footer{display:flex;gap:8px;justify-content:flex-end;padding:10px 12px;border-top:1px solid #eee}
.pc-crop-cols{flex:1;display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:12px;min-height:0}
.pc-crop-left,.pc-crop-right{display:flex;flex-direction:column;min-height:0}
.pc-crop-work{position:relative;flex:1;border:1px dashed #ddd;border-radius:6px;overflow:hidden;background:repeating-conic-gradient(#f6f6f6 0% 25%,#ffffff 0% 50%) 50%/16px 16px}
.pc-crop-img{width:100%;height:100%;object-fit:contain}
.pc-crop-rect{position:absolute;border:2px solid #ff7a00;box-shadow:0 0 0 9999px rgba(0,0,0,.35);display:block!important}
.pc-crop-rect.circle{border-radius:50%}
.pc-crop-handle{position:absolute;width:12px;height:12px;background:#ffffff;border:1px solid #666;border-radius:2px;display:block!important;cursor:n-resize}
.pc-crop-handle:nth-child(1){cursor:nwse-resize}
.pc-crop-handle:nth-child(2){cursor:n-resize}
.pc-crop-handle:nth-child(3){cursor:nesw-resize}
.pc-crop-handle:nth-child(4){cursor:e-resize}
.pc-crop-handle:nth-child(5){cursor:nwse-resize}
.pc-crop-handle:nth-child(6){cursor:s-resize}
.pc-crop-handle:nth-child(7){cursor:nesw-resize}
.pc-crop-handle:nth-child(8){cursor:w-resize}
.pc-crop-controls{margin-top:8px}

@media (max-width:1000px){
  .pc-modal{width:100%!important;height:90vh!important;max-width:100vw;max-height:90vh;border-radius:0}
  .pc-side{grid-template-columns:1fr}
  .pc-bg{height:45vh;min-height:260px}
}

@media (max-width:992px){
  .pc-side{grid-template-columns:1fr}
  .pc-crop-cols{grid-template-columns:1fr}
}

@media (max-width:520px){
  // .pc-line-opts{max-height:220px}
  .pc-line-opt{flex-direction:column;align-items:stretch}
  .pc-line-opt .form-label{min-width:0}
}
`;

    const style = document.createElement('style');
    style.id = id;
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
}
// -----------------------------
// Mounting into all placeholders
// -----------------------------
function mountAll() {
    injectStyles();
    const {product} = getGlobals();
    const customizer = product?.customizer || null;

    // If no customizer for this product, keep placeholders empty
    const nodes = document.querySelectorAll('[data-product-customizer]');
    nodes.forEach((node) => {
        // Skip if already mounted
        if (node.dataset.pcMounted) return;

        // Clear previous content ("Loading...")
        while (node.firstChild) node.removeChild(node.firstChild);
        if (!customizer) return;

        node.dataset.pcMounted = "true";
        const root = createRoot(node);
        root.render(
            <ProductCustomizerProvider>
                <PersonalizeButton/>
                {/* The overlay renders once per provider; multiple providers are OK.
            Each OverlayModal creates its own portal container, so no collisions. */}
                <OverlayModal/>
            </ProductCustomizerProvider>
        );
    });
}

// Skip if already loaded (app embed + legacy script tag during migration)
if (!window.productCustomizerMountAll) {
    window.productCustomizerMountAll = mountAll;

    // Auto-run when script is loaded by the app embed
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mountAll);
    } else {
        mountAll();
    }
}
